/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Einstein recommender name constants matching Business Manager configuration.
 * Used by route loaders (via `fetchProductRecommendations`) and recommendation components.
 *
 * @example
 * ```tsx
 * import { EINSTEIN_RECOMMENDERS } from '@/lib/product/einstein-recommenders';
 *
 * <ProductRecommendations
 *   recommenderName={EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET}
 *   title="You May Also Like"
 * />
 * ```
 */
// RGBARRY-CUSTOMIZATION: recommender names aligned to the brand's live SFRA storefront mapping,
// derived from the SFRA staging content-slot export (slots.xml). Each SFNext carousel is pointed
// at the same Einstein recommender the brand runs in the equivalent SFRA section so the two
// storefronts behave identically. Constant keys are kept stable to avoid churn across consumers;
// only the recommender rule-ids (values) are the source of truth here.
export const EINSTEIN_RECOMMENDERS = {
    /** Similar items modal shown when adding product to cart (SFRA: Customers Also Viewed) */
    ADD_TO_CART_MODAL: 'product-to-product-Customers-who-viewed-also-viewed',
    /** Recently viewed products shown on cart page (SFRA: recently-viewed) */
    CART_RECENTLY_VIEWED: 'recently-viewed',
    /** "You May Also Like" products shown on cart page (SFRA cart slots: complete-the-set) */
    CART_MAY_ALSO_LIKE: 'complete-the-set',
    /** PDP "You May Also Like" — same working recommender as the cart page (complete-the-set) */
    PDP_COMPLETE_SET: 'complete-the-set',
    /** PDP "Recently Viewed" (SFRA: product-similar-category-recommendations-3) */
    PDP_RECENTLY_VIEWED: 'recently-viewed',
    /**
     * Account overview curated carousel. SFRA has no account recommender slot, so this reuses
     * the brand's live `recently-viewed` rule-id (rather than an account-only recommender that
     * doesn't exist in the realm and renders empty).
     */
    ACCOUNT_RECENTLY_VIEWED: 'recently-viewed',
    /**
     * Search no-results carousel. SFRA's `search-no-results-recommendations` slot references the
     * rule-id `viewed-recently`, but that name returns 404 from the Einstein v3 recs API in this
     * realm — only `recently-viewed` exists (200). SFNext always uses `recently-viewed` so the
     * carousel can actually populate rather than mirroring SFRA's broken/legacy rule-id.
     */
    EMPTY_SEARCH_RESULTS_TOP_SELLERS: 'recently-viewed',
    /** Homepage + account curated carousel (SFRA homepage: products-in-all-categories) */
    EMPTY_SEARCH_RESULTS_MOST_VIEWED: 'products-in-all-categories',
} as const;

/**
 * Type representing all valid Einstein recommender names
 */
export type EinsteinRecommenderName = (typeof EINSTEIN_RECOMMENDERS)[keyof typeof EINSTEIN_RECOMMENDERS];
